import { useEffect, useState } from "react";
import { Grid2, Slider } from "@mui/material";
import AddonUsageStyle from "../AddonUsage/AddonUsageStyle.module.scss";
import CustomButton from "../../../../../../GlobalComponents/CustomButton/CustomButton";
import Dropdown from "../../../../../../GlobalComponents/Dropdown/Dropdown";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Actions } from "../../../../../../redux/constants/actionTypes";
import { getStore } from "../../../../../../Utils/storeUtils";
import {
  formatFileSize,
  millisecondsToRoundedHours,
} from "../../../../../../Utils/utils";
import { addons_names } from "../../../../../../Utils/roconConstants";

const AddonUsage = (props) => {
  const {
    icon,
    addonType,
    existingAddons,
    addonCost,
    onAddMore,
    onRemove,
    country,
    onHoursChange,
    onPriceChange,
    addonDetails,
    limit,
    stgLimit,
    userDetails,
    dedicatedHosting,
    storageSiteOptions = [],
    storageSiteIds = [],
    onStorageSiteSelect,
    storageSitesUsage = [],
    performanceSiteOptions = [],
    performanceSiteId,
    onPerformanceSiteSelect,
  } = props;

  // State to manage the count of addons
  const params = useParams();
  const orgId = params.orgId;
  const [count, setCount] = useState(0);
  const [isAddingMore, setIsAddingMore] = useState(false);
  const [sliderValue, setSliderValue] = useState(0);

  const marks = [
    { value: 0, label: "0hr" },
    { value: 15, label: "15hr" },
    { value: 30, label: "30hr" },
    { value: 45, label: "45hr" },
    { value: 60, label: "60hr" },
    { value: 75, label: "75hr" },
  ];

  useEffect(() => {
    const calculateAmounts = () => {
      if (typeof onHoursChange === "function") {
        onHoursChange(sliderValue);
      }

      if (typeof onPriceChange === "function") {
        onPriceChange(10);
      }
    };

    calculateAmounts();
  }, [sliderValue]);

  useEffect(() => {
    const fetchData = () => {
      getStore().dispatch({
        type: Actions.API_STAGING_TIMESTAMP,
        data: {
          orgId: orgId,
          siteId: 0,
        },
        meta: {
          onSuccess: () => {},
          onError: () => {},
        },
      });
    };

    fetchData();

    const intervalId = setInterval(fetchData, 600000);

    return () => clearInterval(intervalId);
  }, [orgId]);

  const stagingCount = addonDetails?.filter(
    (addon) => addon.addonType === "Staging",
  )[0]?.addonCount;

  const performanceSitesCount = addonDetails?.filter(
    (addon) => addon.addonType === "Performance Sites",
  )[0]?.addonCount;

  // Storage is a separate one-time checkout, so it is not combined with other add-ons
  const storageAddon = addonDetails?.find(
    (addon) => addon.addonType === addons_names.STORAGE,
  );
  const storageCount = storageAddon?.addonCount || 0;
  const isStorage = addonType === addons_names.STORAGE;
  const storagePackGb = storageAddon?.packGb || 0;

  // Performance upgrade: one-time, one site, one tier; its count is 0 or 1
  const performanceUpgradeAddon = addonDetails?.find(
    (addon) => addon.addonType === addons_names.PERFORMANCE_UPGRADE,
  );
  const performanceUpgradeCount = performanceUpgradeAddon?.addonCount || 0;
  const isPerformanceUpgrade = addonType === addons_names.PERFORMANCE_UPGRADE;

  const { stagingCredits, usedCreditsTs } = useSelector((state) => ({
    stagingCredits: state.sitesInfo?.credits?.stagingCredits,
    usedCreditsTs: state.sitesInfo?.credits?.usedTs,
  }));

  let creditsUsed = millisecondsToRoundedHours(usedCreditsTs);

  // Function to handle adding more
  const handleAddMore = () => {
    setIsAddingMore(true);
    setCount(1);
    onAddMore();
  };

  // Function to increase count
  const increaseCount = () => {
    if (addonType === "Performance Sites" && limit?.limitOrAvailablity) {
      // Check if the count is less than the remaining limit
      if (count < limit.limitOrAvailablity - existingAddons) {
        setCount(count + 1);
        onAddMore();
      }
    } else {
      setCount(count + 1);
      onAddMore();
    }
  };

  // Function to decrease count
  const decreaseCount = () => {
    if (count === 1) {
      setCount(0);
      setIsAddingMore(false);
      onRemove();
    } else if (count > 1) {
      setCount(count - 1);
      onRemove();
    }
  };

  let currency = country === "USA" ? "$" : "₹";

  const handleSliderChange = (event, newValue) => {
    setSliderValue(newValue);
  };

  const performanceAddon = addonDetails.find(
    (item) => item?.addonType === "Staging",
  );
  const isOtherComponentDisabled = performanceAddon?.addonCount !== 0;

  // Calculate remaining count for "Performance Sites"
  const remainingCount =
    addonType === "Performance Sites" && limit?.limitOrAvailablity
      ? limit.limitOrAvailablity - existingAddons
      : null;

  const stgCredits = parseInt(stgLimit) + stagingCredits;
  const remainingStgCredits = stgCredits - creditsUsed;

  // Per selected site: used is the raw measured size, limit is in GB
  const storageUsageLine = ({ siteName, usage }) =>
    usage?.used !== undefined
      ? `${siteName}: ${formatFileSize(Number(usage.used))} used of ${
          usage.limitOrAvailablity
        } GB`
      : siteName;
  const storageMissingSite = count > 0 && storageSiteIds.length === 0;

  // Don't render Staging or Performance Sites when dedicatedHosting is true
  if (
    dedicatedHosting &&
    (addonType === "Staging" || addonType === "Performance Sites")
  ) {
    return null;
  }

  return (
    <Grid2 className={AddonUsageStyle.container}>
      <Grid2 className={AddonUsageStyle.addonIcon}>{icon}</Grid2>
      <Grid2 className={AddonUsageStyle.addonHeader}>
        <Grid2 className={AddonUsageStyle.addonTypeLabel}>
          {addonType === "Waf" ? "WAF" : addonType}
        </Grid2>
        {existingAddons > 0 && (
          <Grid2 className={AddonUsageStyle.addonCountLabel}>
            {addonType === "Staging" &&
              remainingStgCredits !== 0 &&
              `${remainingStgCredits} hours remaining`}
            {addonType !== "Staging" &&
              addonType !== "Waf" &&
              `${existingAddons} in use`}{" "}
          </Grid2>
        )}
      </Grid2>
      <Grid2 className={AddonUsageStyle.textLabel}>
        {addonType === "Staging"
          ? "Test updates, features, and design changes in a secure staging space before deploying them live."
          : addonType === "Performance Sites"
            ? "Optimize high-traffic websites with enhanced speed, stability, and responsiveness for a seamless user experience."
            : addonType === "Waf"
              ? "Protect your website from cyber threats with advanced security against DDoS attacks, SQL injections, and malware."
              : isStorage
                ? "Add more disk space to one of your EBS sites. The site's disk grows automatically after payment."
                : isPerformanceUpgrade
                  ? "Move one of your EBS sites to a larger instance for faster page loads under heavy traffic. The site is resized automatically after payment."
                  : ""}
      </Grid2>
      <Grid2 className={AddonUsageStyle.costLabel}>
        {addonType === "Staging"
          ? `${currency}${addonCost} / 15 Hours`
          : addonType === "Performance Sites"
            ? `${currency}${addonCost} / site (no additional visitors)`
            : isStorage
              ? `${currency}${addonCost} / ${storagePackGb} GB (one-time)`
              : isPerformanceUpgrade
                ? `${currency}${addonCost} / site (one-time)`
                : ""}
      </Grid2>

      {/* Storage: pick the EBS sites whose disks grow (every site gets the same packs);
          the card is only rendered when the account has an EBS site */}
      {isStorage && (
        <Grid2>
          <Dropdown
            options={storageSiteOptions}
            title="Select sites"
            placeholder="Select your sites"
            value={storageSiteIds}
            onSelect={(ids) => onStorageSiteSelect(ids)}
            multiple={true}
            error={storageMissingSite}
            errorMessage={
              storageMissingSite ? "Select at least one site to continue" : ""
            }
          />
          {storageSitesUsage.map((siteUsage) => (
            <Grid2 key={siteUsage.siteId} className={AddonUsageStyle.textLabel}>
              {storageUsageLine(siteUsage)}
              {count > 0 && ` (+${count * storagePackGb} GB)`}
            </Grid2>
          ))}
        </Grid2>
      )}

      {/* Performance upgrade: pick the EBS site to move to the larger instance;
          the card is only rendered when an EBS site is still eligible */}
      {isPerformanceUpgrade && (
        <Grid2>
          <Dropdown
            options={performanceSiteOptions}
            title="Select site"
            placeholder="Select your site"
            value={performanceSiteId}
            onSelect={(opt) => onPerformanceSiteSelect(opt)}
            error={count > 0 && !performanceSiteId}
            errorMessage={
              count > 0 && !performanceSiteId ? "Select a site to continue" : ""
            }
          />
        </Grid2>
      )}

      <Grid2 className={AddonUsageStyle.footer}>
        {isPerformanceUpgrade ? (
          <Grid2 className={AddonUsageStyle.addonButton}>
            <Grid2 className={AddonUsageStyle.addonCostLabel}>
              {currency}
              {addonCost}
            </Grid2>
            {isAddingMore ? (
              <CustomButton
                className={AddonUsageStyle.button}
                onClick={decreaseCount}
              >
                Remove
              </CustomButton>
            ) : (
              <CustomButton
                className={AddonUsageStyle.button}
                onClick={handleAddMore}
                disabled={
                  performanceSiteOptions.length === 0 ||
                  (stagingCount || 0) !== 0 ||
                  (performanceSitesCount || 0) !== 0 ||
                  storageCount !== 0
                }
              >
                Upgrade
              </CustomButton>
            )}
          </Grid2>
        ) : isStorage ? (
          <Grid2 className={AddonUsageStyle.addonButton}>
            <Grid2 className={AddonUsageStyle.addonCostLabel}>
              {currency}
              {addonCost}
            </Grid2>
            {isAddingMore ? (
              <>
                <CustomButton
                  className={AddonUsageStyle.button}
                  onClick={decreaseCount}
                  disabled={count === 0}
                >
                  -
                </CustomButton>
                <Grid2 className={AddonUsageStyle.buttonLabel}>{count}</Grid2>
                <CustomButton
                  className={AddonUsageStyle.button}
                  onClick={increaseCount}
                >
                  +
                </CustomButton>
              </>
            ) : (
              <CustomButton
                className={AddonUsageStyle.button}
                onClick={handleAddMore}
                disabled={
                  storageSiteOptions.length === 0 ||
                  (stagingCount || 0) !== 0 ||
                  (performanceSitesCount || 0) !== 0 ||
                  performanceUpgradeCount !== 0
                }
              >
                Add More
              </CustomButton>
            )}
          </Grid2>
        ) : addonType !== "Waf" && addonType !== "Staging" ? (
          <Grid2 className={AddonUsageStyle.addonButton}>
            <Grid2 className={AddonUsageStyle.addonCostLabel}>
              {currency}
              {addonCost}/M
            </Grid2>
            {isAddingMore ? (
              <>
                <CustomButton
                  className={AddonUsageStyle.button}
                  onClick={decreaseCount}
                  disabled={count === 0}
                >
                  -
                </CustomButton>
                <Grid2 className={AddonUsageStyle.buttonLabel}>{count}</Grid2>{" "}
                {/* Display current count */}
                <CustomButton
                  className={AddonUsageStyle.button}
                  onClick={increaseCount}
                  disabled={
                    addonType === "Performance Sites" && count >= remainingCount
                  }
                >
                  +
                </CustomButton>
              </>
            ) : (
              count < limit.limitOrAvailablity - existingAddons && (
                <CustomButton
                  className={AddonUsageStyle.button}
                  onClick={handleAddMore}
                  disabled={
                    isOtherComponentDisabled ||
                    stagingCount !== 0 ||
                    storageCount !== 0 ||
                    performanceUpgradeCount !== 0
                  }
                >
                  Add More
                </CustomButton>
              )
            )}
          </Grid2>
        ) : addonType !== "Staging" ? (
          <span className={AddonUsageStyle.freeLabel}>FREE</span>
        ) : (
          ""
        )}

        {/* Staging Slider */}
        {addonType === "Staging" && (
          <Grid2 className={AddonUsageStyle.slider}>
            <Slider
              aria-label="Plan Slider"
              value={sliderValue}
              step={null}
              marks={marks}
              min={-10}
              max={85}
              valueLabelDisplay="off"
              onChange={handleSliderChange}
              disabled={
                performanceSitesCount !== 0 ||
                storageCount !== 0 ||
                performanceUpgradeCount !== 0
              }
              sx={{
                width: "90%",
                color: "#1976d2",
                height: 8,
                opacity:
                  performanceSitesCount !== 0 ||
                  storageCount !== 0 ||
                  performanceUpgradeCount !== 0
                    ? 0.5
                    : 1,
                "& .MuiSlider-thumb": {
                  height: 20,
                  width: 20,
                  backgroundColor: "white",
                  border: "5px solid #1976d2",
                  borderRadius: "50%",
                  "&:hover, &.Mui-focusVisible": {
                    boxShadow: "inherit",
                  },
                  "&.Mui-active": {
                    boxShadow: "inherit",
                  },
                },
                "& .MuiSlider-track": {
                  height: 8,
                  borderRadius: 4,
                },
                "& .MuiSlider-rail": {
                  height: 8,
                  borderRadius: 4,
                  backgroundColor:
                    userDetails?.theme === "dark" ? "#fff" : "#000",
                },
                "& .MuiSlider-mark": {
                  height: 8,
                  width: 8,
                  backgroundColor: "white",
                  borderRadius: "50%",
                },
                "& .MuiSlider-markLabel": {
                  color: userDetails?.theme === "dark" ? "#fff" : "#000",
                  fontSize: "14px",
                  fontWeight: 500,
                },
              }}
            />
          </Grid2>
        )}
      </Grid2>
    </Grid2>
  );
};

export default AddonUsage;
